import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { RequestError } from '#errors/index.ts';
import { trimTrailingSlash } from '#utils/index.ts';
import { isAck, toSocketAuth, willReconnect } from './socket.helpers.ts';
import type {
  TClientEvent,
  TClientMessage,
  TServerEvent,
  TSocket,
  TSocketOptions,
  TSocketSendOptions,
  TSocketStatus,
  TSocketTransport,
} from './socket.types.ts';

const DEFAULT_PATH = '/websocket';
const defaultTransports: TSocketTransport[] = ['websocket'];

const sendWithAck = async (
  socket: Socket,
  event: string,
  envelope: unknown,
  { timeout }: TSocketSendOptions = {},
): Promise<void> => {
  if (!socket.connected) {
    throw new RequestError({ kind: 'network', retryable: true });
  }

  const emitter = timeout === undefined ? socket : socket.timeout(timeout);
  let ack: unknown;

  try {
    ack = await emitter.emitWithAck(event, envelope);
  } catch (cause) {
    throw new RequestError(
      { kind: socket.connected ? 'timeout' : 'network', retryable: true },
      { cause },
    );
  }

  if (!isAck(ack)) {
    throw new RequestError({ kind: 'decode', retryable: false });
  }

  if (!ack.ok) {
    throw new RequestError({
      kind: 'refused',
      retryable: false,
      reason: ack.reason,
    });
  }
};

export const createSocket = ({
  url,
  path = DEFAULT_PATH,
  auth,
  withCredentials = true,
  transports = defaultTransports,
  ...passthrough
}: TSocketOptions): TSocket => {
  const baseUrl = trimTrailingSlash(url);
  const socket = io(baseUrl, {
    ...passthrough,
    path,
    auth: toSocketAuth(auth),
    withCredentials,
    transports,
    autoConnect: false,
  });

  const envelopeOf = <TEvent extends TClientEvent>(
    event: TEvent,
    message: TClientMessage<TEvent>,
  ) => ({ type: event, ...message });

  const send: TSocket['send'] = (event, message, options) =>
    sendWithAck(socket, event, envelopeOf(event, message), options);

  const listen = (
    subscribe: (name: string, listener: (envelope: unknown) => void) => void,
  ): TSocket['on'] => {
    return (event, listener) => {
      const name: string = event;

      const forward = (envelope: unknown) => {
        listener(envelope as Parameters<typeof listener>[0]);
      };

      subscribe(name, forward);

      return () => {
        socket.off(name, forward);
      };
    };
  };

  return {
    get id() {
      return socket.id;
    },
    get connected() {
      return socket.connected;
    },
    get recovered() {
      return socket.recovered;
    },
    connect: () => {
      socket.connect();
    },
    disconnect: () => {
      socket.disconnect();
    },
    on: listen((name, listener) => socket.on(name, listener)),
    once: listen((name, listener) => socket.once(name, listener)),
    onAny: (listener) => {
      const forward = (event: string, envelope: unknown) => {
        listener(event as TServerEvent, envelope);
      };

      socket.onAny(forward);

      return () => {
        socket.offAny(forward);
      };
    },
    onStatus: (listener) => {
      const report = (status: TSocketStatus) => listener(status);
      const onConnect = () =>
        report({ state: 'connected', recovered: socket.recovered });

      const onDisconnect = (reason: string) =>
        report({ state: 'disconnected', willReconnect: willReconnect(reason) });

      const onConnectError = (error: Error) =>
        report({ state: 'error', error, willRetry: socket.active });

      const onAttempt = (attempt: number) =>
        report({ state: 'reconnecting', attempt });

      const onFailed = () => report({ state: 'failed' });

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('connect_error', onConnectError);
      socket.io.on('reconnect_attempt', onAttempt);
      socket.io.on('reconnect_failed', onFailed);

      return () => {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('connect_error', onConnectError);
        socket.io.off('reconnect_attempt', onAttempt);
        socket.io.off('reconnect_failed', onFailed);
      };
    },
    emit: (event, message) => {
      socket.emit(event, envelopeOf(event, message));
    },
    send,
  };
};
